import React, { Component } from 'react';
import { Form, Field } from 'react-final-form';
import PropTypes from 'prop-types';
import PropTypesImmutable from 'react-immutable-proptypes';
import {
  DateField,
  ClassificatorField,
  CheckboxField,
  SelectField,
} from '../../atoms';
import { FormActions, TableActions } from '../../components';
import { connect } from 'react-redux';
import { Collapse, Row, Col, Button } from 'antd';
import { FORM_ACCOUNT_OVERVIEW_FILTER } from '../../constants/formKeys';
import { legalEntityFields } from '../../constants/legalEntity';
import { formAccountOverviewFilter } from '../../constants/forms/formAccountOverviewFilter';
import * as actions from '../../actions/actionCreators/accountActionCreators';
import * as productionUnitActions from '../../actions/actionCreators/productionUinitActionCreators';
import { getRole, getUserLegalEntity } from '../../reducers/userReducer';
import { getProductionUnitListEntities } from '../../reducers/legalEntityProductionUnitsReducer';
import _ from 'lodash';
import { FORMAT_DEFAULT } from '../../utils';
import { links } from '../../utils';
import { downloadFileByPostURL } from '../../utils/files';
import { isAdmin } from '../../utils/roles';
import gasType from '../../constants/classificators/gasType';
import { productionUnitFields } from '../../constants/productionUnit';
import { accountTableFields } from '../../constants/accountTable';
import { tCommon } from '../../i18n';
import dayjs from '../../utils/dayjs';
import FormStateToRedux from '../FormStateToRedux';
import {
  updateFormState,
  formValueSelector,
} from '../../reducers/reactFinalFormReducer';

const { Panel } = Collapse;

const getInitialValues = (accountIds, legalEntity, userRole, manufacture) => {
  const isUserAdmin = isAdmin(userRole);
  let gas;
  if (
    (legalEntity &&
      legalEntity.get('manufacturer') === true &&
      legalEntity.get('hydrogenManufacturer') === true) ||
    isUserAdmin ||
    !manufacture
  ) {
    gas = null;
  } else if (legalEntity.get('manufacturer') === true) {
    gas = gasType.BIOMETHANE_GAS_TYPE;
  } else if (legalEntity.get('hydrogenManufacturer') === true) {
    gas = gasType.HYDROGEN_GAS_TYPE;
  }
  const ids = accountIds.filter((id) => id !== null);
  return {
    [formAccountOverviewFilter.GROUP_BY_EXPIRATION]: true,
    [formAccountOverviewFilter.BALANCE_DATE]: dayjs().format(FORMAT_DEFAULT),
    [formAccountOverviewFilter.GAS_TYPE]: gas,
    accountIds: ids,
  };
};

export class AccountOverviewFilter extends Component {
  static propTypes = {
    t: PropTypes.func.isRequired,
    fetchAccountOverview: PropTypes.func.isRequired,
    fetchLegalEntityProductionUnits: PropTypes.func.isRequired,
    legalEntity: PropTypesImmutable.map.isRequired,
    productionUnitList: PropTypesImmutable.list,
    accountIds: PropTypes.array,
    filterValues: PropTypes.object,
    manufacture: PropTypes.bool,
    changeField: PropTypes.func,
    resetFields: PropTypes.func.isRequired,
    renderFields: PropTypes.func.isRequired,
    updateFormState: PropTypes.func.isRequired,
    initialValues: PropTypes.object,
    userRole: PropTypes.string,
    initialize: PropTypes.func,
  };

  abortController = null;

  fetchWithAbort = (values) => {
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();
    this.props.fetchAccountOverview(values, {
      signal: this.abortController.signal,
    });
  };

  componentDidMount = () => {
    const {
      fetchLegalEntityProductionUnits,
      legalEntity,
      initialValues,
      updateFormState,
    } = this.props;

    this._initialValues = initialValues;
    updateFormState(FORM_ACCOUNT_OVERVIEW_FILTER, { values: initialValues });
    fetchLegalEntityProductionUnits(legalEntity.get(legalEntityFields.ID));
    this.fetchWithAbort(initialValues);
  };

  componentDidUpdate = (prevProps) => {
    const { accountIds, updateFormState } = this.props;
    if (!_.isEqual(accountIds, prevProps.accountIds)) {
      this._initialValues = this.props.initialValues;
      if (this._form) {
        this._form.restart(this._initialValues);
      }
      updateFormState(FORM_ACCOUNT_OVERVIEW_FILTER, {
        values: this._initialValues,
      });
      this.fetchWithAbort(this._initialValues);
    }
  };

  componentWillUnmount = () => {
    if (this.abortController) {
      this.abortController.abort();
    }
  };

  onClear = (form) => {
    const { updateFormState, resetFields } = this.props;
    resetFields();
    form.restart(this._initialValues);
    updateFormState(FORM_ACCOUNT_OVERVIEW_FILTER, {
      values: this._initialValues,
    });
  };

  onGasTypeChange = (form) => {
    const { resetFields } = this.props;
    resetFields();
    form.change(formAccountOverviewFilter.FUEL_TYPE, null);
    form.change(formAccountOverviewFilter.CLEANING_TECHNOLOGY_TYPE, null);
    form.change(formAccountOverviewFilter.HYDROGEN_FUEL_TYPE, null);
    form.change(formAccountOverviewFilter.HYDROGEN_TECHNOLOGY_TYPE, null);
    form.change(formAccountOverviewFilter.GROUP_BY_TECHNOLOGY, false);
    form.change(formAccountOverviewFilter.GROUP_BY_FUEL_TYPE, false);
    form.change(formAccountOverviewFilter.GROUP_BY_HYDROGEN_TECHNOLOGY, false);
    form.change(formAccountOverviewFilter.GROUP_BY_HYDROGEN_FUEL_TYPE, false);
    form.change(formAccountOverviewFilter.PRODUCTION_UNIT_ID, null);
  };

  onSubmit = (values) => {
    this.props.renderFields();
    const updatedValues = { ...values, accountIds: this.props.accountIds };
    if (
      updatedValues.sustainableBiofuel !== undefined &&
      updatedValues.sustainableBiofuel !== null
    ) {
      updatedValues.sustainableBiofuel =
        updatedValues.sustainableBiofuel === 'true';
    }
    this.props.fetchAccountOverview(updatedValues);
  };

  handleExportTransactions = () => {
    const { filterValues, accountIds } = this.props;

    downloadFileByPostURL(
      links.goSearchExportCsv,
      filterValues ? { ...filterValues.toJS(), accountIds } : { accountIds },
      'account-overview.csv'
    );
  };

  render() {
    const { t, productionUnitList, manufacture, changeField } = this.props;

    return (
      <React.Fragment>
        <Form
          onSubmit={this.onSubmit}
          initialValues={this._initialValues}
          render={({ handleSubmit, form, values }) => {
            this._form = form;
            const gasFieldValue = values[formAccountOverviewFilter.GAS_TYPE];
            return (
              <Collapse defaultActiveKey={['filters']}>
                <Panel key="filters" header={t('filter.title')}>
                  <form onSubmit={handleSubmit}>
                    <FormStateToRedux form={FORM_ACCOUNT_OVERVIEW_FILTER} />
                    <Row gutter={32}>
                      <Col span={16}>
                        <Row gutter={32}>
                          <Col span={12}>
                            <Field
                              className="date-field"
                              name={formAccountOverviewFilter.VALID_UNTIL}
                              component={DateField}
                              label={t('overview.filter.validUntil')}
                              hasFeedback={false}
                            />
                          </Col>
                          <Col span={12}>
                            <Field
                              label={t('overview.filter.gas')}
                              name={formAccountOverviewFilter.GAS_TYPE}
                              component={ClassificatorField}
                              classificatorType={
                                ClassificatorField.types.gasType
                              }
                              hasFeedback={false}
                              onChange={() => this.onGasTypeChange(form)}
                            />
                          </Col>
                        </Row>
                        {gasFieldValue === gasType.BIOMETHANE_GAS_TYPE && (
                          <Row gutter={32}>
                            <Col span={12}>
                              <Field
                                label={t('overview.filter.fuelType')}
                                name={formAccountOverviewFilter.FUEL_TYPE}
                                component={ClassificatorField}
                                classificatorType={
                                  ClassificatorField.types.fuelType
                                }
                                hasFeedback={false}
                              />
                            </Col>
                            <Col span={12}>
                              <Field
                                label={t(
                                  'overview.filter.cleaningTechnologyType'
                                )}
                                name={
                                  formAccountOverviewFilter.CLEANING_TECHNOLOGY_TYPE
                                }
                                component={ClassificatorField}
                                classificatorType={
                                  ClassificatorField.types
                                    .cleaningTechnologyType
                                }
                                hasFeedback={false}
                              />
                            </Col>
                          </Row>
                        )}
                        {gasFieldValue === gasType.HYDROGEN_GAS_TYPE && (
                          <Row gutter={32}>
                            <Col span={12}>
                              <Field
                                label={t('overview.filter.hydrogenType')}
                                name={
                                  formAccountOverviewFilter.HYDROGEN_FUEL_TYPE
                                }
                                component={ClassificatorField}
                                classificatorType={
                                  ClassificatorField.types.hydrogenFuelType
                                }
                                hasFeedback={false}
                              />
                            </Col>
                            <Col span={12}>
                              <Field
                                label={t(
                                  'overview.filter.hydrogenTechnologyType'
                                )}
                                name={
                                  formAccountOverviewFilter.HYDROGEN_TECHNOLOGY_TYPE
                                }
                                component={ClassificatorField}
                                classificatorType={
                                  ClassificatorField.types
                                    .hydrogenTechnologyType
                                }
                                hasFeedback={false}
                              />
                            </Col>
                          </Row>
                        )}
                        <Row gutter={32}>
                          {manufacture && (
                            <Col span={12}>
                              <Field
                                label={t('overview.filter.productionUnit')}
                                name={
                                  formAccountOverviewFilter.PRODUCTION_UNIT_ID
                                }
                                component={SelectField}
                                options={productionUnitList.toJS()}
                                valueKey={productionUnitFields.ID}
                                labelKey={productionUnitFields.NAME}
                                hasFeedback={false}
                              />
                            </Col>
                          )}
                          {gasFieldValue !== gasType.HYDROGEN_GAS_TYPE && (
                            <Col span={12}>
                              <Field
                                label={t('overview.filter.sustainableBiofuel')}
                                name={
                                  formAccountOverviewFilter.SUSTAINABLE_BIOFUEL
                                }
                                component={SelectField}
                                options={[
                                  {
                                    value: 'true',
                                    label: tCommon(
                                      'classificators.boolean.true'
                                    ),
                                  },
                                  {
                                    value: 'false',
                                    label: tCommon(
                                      'classificators.boolean.false'
                                    ),
                                  },
                                ]}
                                valueKey="value"
                                labelKey="label"
                                hasFeedback={false}
                                allowClear
                              />
                            </Col>
                          )}
                        </Row>
                      </Col>
                      <Col span={8}>
                        <span className="page-AccountOverviewPage__groupByLabel">
                          {t('overview.filter.groupBy')}
                        </span>
                        <div className="page-AccountOverviewPage__groupBy">
                          {gasFieldValue === gasType.BIOMETHANE_GAS_TYPE && (
                            <Field
                              name={
                                formAccountOverviewFilter.GROUP_BY_TECHNOLOGY
                              }
                              type="checkbox"
                              component={CheckboxField}
                              hasFeedback={false}
                              checkboxText={t(
                                'overview.filter.groupByTechnology'
                              )}
                              onChange={(e) => {
                                changeField(
                                  accountTableFields.TECHNOLOGY,
                                  e.target.checked
                                );
                              }}
                            />
                          )}
                          {gasFieldValue === gasType.BIOMETHANE_GAS_TYPE && (
                            <Field
                              name={
                                formAccountOverviewFilter.GROUP_BY_FUEL_TYPE
                              }
                              type="checkbox"
                              component={CheckboxField}
                              hasFeedback={false}
                              checkboxText={t(
                                'overview.filter.groupByFuelType'
                              )}
                              onChange={(e) => {
                                changeField(
                                  accountTableFields.FUEL_TYPE,
                                  e.target.checked
                                );
                              }}
                            />
                          )}
                          {gasFieldValue === gasType.HYDROGEN_GAS_TYPE && (
                            <Field
                              name={
                                formAccountOverviewFilter.GROUP_BY_HYDROGEN_TECHNOLOGY
                              }
                              type="checkbox"
                              component={CheckboxField}
                              hasFeedback={false}
                              checkboxText={t(
                                'overview.filter.groupByHydrogenTechnology'
                              )}
                              onChange={(e) => {
                                changeField(
                                  accountTableFields.HYDROGEN_TECHNOLOGY,
                                  e.target.checked
                                );
                              }}
                            />
                          )}
                          {gasFieldValue === gasType.HYDROGEN_GAS_TYPE && (
                            <Field
                              name={
                                formAccountOverviewFilter.GROUP_BY_HYDROGEN_FUEL_TYPE
                              }
                              type="checkbox"
                              component={CheckboxField}
                              hasFeedback={false}
                              checkboxText={t(
                                'overview.filter.groupByHydrogeFuelType'
                              )}
                              onChange={(e) => {
                                changeField(
                                  accountTableFields.HYDROGEN_FUEL_TYPE,
                                  e.target.checked
                                );
                              }}
                            />
                          )}
                          <Field
                            name={formAccountOverviewFilter.GROUP_BY_EXPIRATION}
                            type="checkbox"
                            component={CheckboxField}
                            hasFeedback={false}
                            disabled
                            checkboxText={t(
                              'overview.filter.groupByExpiration'
                            )}
                          />
                        </div>
                      </Col>
                    </Row>
                    <FormActions>
                      <Button onClick={() => this.onClear(form)}>
                        {t('filter.clearBtn')}
                      </Button>
                      <Button type="primary" htmlType="submit">
                        {t('filter.submitBtn')}
                      </Button>
                    </FormActions>
                  </form>
                </Panel>
              </Collapse>
            );
          }}
        />
        <TableActions>
          <Button onClick={this.handleExportTransactions}>
            {t('exportCsvBtn')}
          </Button>
        </TableActions>
        {/*<b>{t('overview.info')}</b>*/}
      </React.Fragment>
    );
  }
}

const getValues = formValueSelector(FORM_ACCOUNT_OVERVIEW_FILTER);

const mapStateToProps = (state, ownProps) => {
  const legalEntity = getUserLegalEntity(state);
  const userRole = getRole(state);
  return {
    legalEntity: legalEntity,
    productionUnitList: getProductionUnitListEntities(state),
    initialValues: getInitialValues(
      ownProps.accountIds,
      legalEntity,
      userRole,
      ownProps.manufacture
    ),
    filterValues: getValues(state),
  };
};

const mapDispatchToProps = {
  fetchLegalEntityProductionUnits:
    productionUnitActions.fetchLegalEntityProductionUnits,
  fetchAccountOverview: actions.fetchAccountOverview,
  updateFormState,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AccountOverviewFilter);
